import { DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterLink } from '@angular/router';
import { EMPTY, catchError, interval, startWith, switchMap } from 'rxjs';
import { AuthService } from '../../core/auth/auth.service';
import { NotificationsService } from '../../core/api/notifications.service';
import { AppNotification } from '../../core/models/notification.models';

/** How often the bell asks for the unread count. */
const POLL_MS = 30_000;
/** How many notifications the dropdown lists; the rest are on the notifications page. */
const DROPDOWN_SIZE = 2;
/** How many of the newest notifications are scanned for unread ones when the dropdown opens. */
const UNREAD_SCAN_SIZE = 50;

/**
 * The bell in the top bar: unread count, a dropdown with the latest notifications (unread highlighted),
 * and a link to the full notifications page.
 */
@Component({
  selector: 'app-notification-bell',
  imports: [DatePipe, MatBadgeModule, MatButtonModule, MatIconModule, MatMenuModule, RouterLink],
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.scss',
})
export class NotificationBell {
  private readonly notifications = inject(NotificationsService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly unreadCount = signal(0);
  readonly items = signal<AppNotification[]>([]);
  readonly loading = signal(false);
  readonly loadFailed = signal(false);

  constructor() {
    // Only asks while signed in, so the bell does not call the API from the login page.
    interval(POLL_MS)
      .pipe(
        startWith(0),
        switchMap(() => (this.auth.isAuthenticated() ? this.notifications.unreadCount().pipe(catchError(() => EMPTY)) : EMPTY)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((count) => this.unreadCount.set(count));
  }

  /** Opening the dropdown loads the unread items, so the list is never older than the click. */
  onOpen(): void {
    this.loading.set(true);
    this.loadFailed.set(false);
    // Same request as the notifications page; unread items are picked here so older unread ones are not missed.
    this.notifications.list(1, UNREAD_SCAN_SIZE).subscribe({
      next: (page) => {
        this.items.set(page.items.filter((item) => !item.isRead).slice(0, DROPDOWN_SIZE));
        this.unreadCount.set(page.unreadCount);
        this.loading.set(false);
      },
      error: () => {
        this.loadFailed.set(true);
        this.loading.set(false);
      },
    });
  }

  /** Marks the item read (it leaves the dropdown and the count drops at once), then opens it on the notifications page. */
  open(item: AppNotification): void {
    this.items.update((list) => list.filter((i) => i.id !== item.id));
    this.unreadCount.update((count) => Math.max(0, count - 1));
    this.notifications.markRead(item.id).subscribe({ error: () => undefined });

    void this.router.navigate(['/notifications'], { queryParams: { highlight: item.id } });
  }
}
